import React from 'react';
import { Star, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Review } from '@/hooks/useReviews';

interface ReviewCardProps {
  review: Review;
  reviewerName?: string;
  showReviewerType?: boolean;
}

const StarDisplay: React.FC<{ rating: number; size?: 'sm' | 'md' }> = ({ 
  rating, 
  size = 'sm' 
}) => {
  const starSize = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${starSize} ${
            star <= rating
              ? 'fill-yellow-400 text-yellow-400'
              : 'text-muted-foreground'
          }`}
        />
      ))}
    </div>
  );
};

export const ReviewCard: React.FC<ReviewCardProps> = ({
  review,
  reviewerName = "Anonymous",
  showReviewerType = false
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return '1 day ago';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} week${Math.floor(diffInDays / 7) > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        {/* Overall Rating */}
        <div className="flex items-center gap-2">
          <StarDisplay rating={review.overall_rating} size="md" />
          <span className="font-medium">Overall Rating</span>
        </div>

        {/* Written Review */}
        {review.written_review && (
          <p className="text-sm text-foreground leading-relaxed">
            {review.written_review}
          </p>
        )}

        {/* Detailed Ratings */}
        {(review.quality_rating || review.timeliness_rating || review.communication_rating) && (
          <div className="space-y-2 border-t pt-3">
            {review.quality_rating && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Quality:</span>
                <StarDisplay rating={review.quality_rating} />
              </div>
            )}
            {review.timeliness_rating && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Timeliness:</span>
                <StarDisplay rating={review.timeliness_rating} />
              </div>
            )}
            {review.communication_rating && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Communication:</span>
                <StarDisplay rating={review.communication_rating} />
              </div>
            )}
          </div>
        )}

        {/* Review Metadata */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground border-t pt-3">
          {review.is_verified && (
            <>
              <CheckCircle className="h-3 w-3 text-green-600" />
              <span>Verified</span>
              <span>•</span>
            </>
          )}
          <span>{reviewerName}</span>
          {showReviewerType && (
            <>
              <span>•</span>
              <span className="capitalize">{review.reviewer_type}</span>
            </>
          )}
          <span>•</span>
          <span>{formatDate(review.published_at || review.created_at)}</span>
        </div>
      </CardContent>
    </Card>
  );
};